/**
 * structure_seat — placed deployed-structure seats + keep-out discs.
 *
 * StructureRenderModule seats structures footprint-aware (terrainSeat over the
 * scaled model bbox — high-relief audit 2026-08-05); DecorationModule composes
 * item transforms on the SAME parent origin, so it must read the exact seated
 * Y, not re-derive a single-point groundY (the two disagree on slopes by the
 * footprint-min delta, shearing decor off its shelf). The entry also carries
 * the structure's position + footprint radius so versona rendering can project
 * NPC bodies out of deed buildings (server NPC movement is geometry-blind).
 * Keyed by structureId; absent → the structure isn't placed yet.
 */
export interface StructureSeat { y: number; x: number; z: number; r: number }

export const structureSeats = new Map<string, StructureSeat>();

export function setStructureSeat(structureId: string, seat: StructureSeat): void {
  structureSeats.set(structureId, seat);
}

export function clearStructureSeat(structureId: string): void {
  structureSeats.delete(structureId);
}

/** Project (x,z) out of every placed structure's keep-out disc (+margin). */
export function structureClearance(x: number, z: number, margin = 1.2): { x: number; z: number } | null {
  let moved = false;
  for (const s of structureSeats.values()) {
    const dx = x - s.x;
    const dz = z - s.z;
    const keep = s.r + margin;
    if (dx * dx + dz * dz >= keep * keep) continue;
    const d = Math.hypot(dx, dz);
    const ux = d > 1e-6 ? dx / d : 1;
    const uz = d > 1e-6 ? dz / d : 0;
    x = s.x + ux * keep;
    z = s.z + uz * keep;
    moved = true;
  }
  return moved ? { x, z } : null;
}
